import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { JsonScriptId, Endpoint } from '@/constants';
import { useGetCart } from '@/hooks/useGetCart';
import type { CheckoutData, PaymentOptionType } from '@/types';
import { getEmbeddedJson } from '@/utils/getEmbeddedJson';
import type { CheckoutContextValue, ProviderProps } from './types';
import { useUser } from './userContext';

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

export const CheckoutProvider = ({
  children,
  isCheckoutPage = false,
}: ProviderProps) => {
  const { isAuthenticated } = useUser();

  const initialCheckoutData: CheckoutData = getEmbeddedJson(
    JsonScriptId.Checkout,
  );

  const [checkoutData, setCheckoutData] = useState(initialCheckoutData);
  const [paymentOption, setPaymentOption] = useState<PaymentOptionType>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [giftCardsLoaded, setGiftCardsLoaded] = useState(false);

  const isGetCartEnabled = isCheckoutPage
    ? (isAuthenticated && giftCardsLoaded) || !isAuthenticated
    : true;

  const { cartData, isPending } = useGetCart(isGetCartEnabled);

  if (!isPending && cartData && JSON.stringify(cartData) !== JSON.stringify(checkoutData)) {
    setCheckoutData(cartData);
  }

  // ✅ ADD: Method to refresh cart data from server
  const refreshCheckoutData = useCallback(async () => {
    if (!isRefreshing) {
      try {
        setIsRefreshing(true);
        console.log('CheckoutContext: Refreshing checkout data...');

        const response = await fetch(Endpoint.GetCart, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to refresh checkout data: ${response.status}`,
          );
        }

        const cartData = await response.json();

        // Map cart data to checkout data format
        const newCheckoutData: CheckoutData = {
          amounts: cartData.amounts ?? cartData.paymentAmounts ?? {},
          cartId: cartData.cartId ?? '',
          cartName: cartData.cartName ?? '',
          clickAndCollectLocations: cartData.clickAndCollectLocations ?? [],
          discountCode: cartData.discountCode ?? '',
          emailAddress: cartData.emailAddress ?? '',
          hasBalance: cartData.hasBalance ?? false,
          isClickAndCollectAvailable:
            cartData.isClickAndCollectAvailable ?? false,
          items: cartData.items ?? [],
          loyaltyCardId: cartData.loyaltyCardId ?? '',
          promotions: cartData.promotions ?? [],
          shippingMethod: cartData.shippingMethod ?? '',
        };

        // Update the checkout data - this will trigger Google Pay to update
        setCheckoutData(newCheckoutData);
        console.log(
          'CheckoutContext: Checkout data refreshed successfully',
          newCheckoutData,
        );
      } catch (error) {
        console.error(
          'CheckoutContext: Failed to refresh checkout data:',
          error,
        );
        // Don't update state on error - keep existing data
      } finally {
        setIsRefreshing(false);
      }
    }
  }, [isRefreshing]);

  const value = useMemo(
    () => ({
      checkoutData,
      giftCardsLoaded,
      isRefreshing, // ← Loading state for UI feedback
      paymentOption,
      refreshCheckoutData, // ← New method for triggering updates
      setCheckoutData,
      setGiftCardsLoaded,
      setPaymentOption,
    }),
    [
      checkoutData,
      giftCardsLoaded,
      isRefreshing,
      paymentOption,
      refreshCheckoutData,
    ],
  );

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);

  if (context === undefined || context === null) {
    throw new Error(
      'useCheckout must be used within a CheckoutContext Provider',
    );
  }

  return context;
};
